import React, { useMemo, useState } from 'react';
import { useGetIdentity, useNotify, useRefresh } from 'react-admin';
import { ClipboardCheck, Check, X, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { applyFields, dismissWorkItem, ApplyFieldUpdate } from './workItemsApi';

interface ProposedUpdate {
  dataPath: string;
  label: string;
  role: string;
  column: string;
  client_id: number;
  value: string;
  raw: string;
  current_value: unknown;
  warning: string | null;
}
interface HeldUpdate {
  dataPath: string;
  label: string;
  role: string;
  value: string;
  reason?: string;
}

export interface IngestWorkItem {
  id: string;
  metadata?: {
    proposed_updates?: ProposedUpdate[];
    held?: HeldUpdate[];
  } | null;
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

/**
 * The ingest card — the agent parsed paperwork fields out of the family's reply.
 * Each proposed write shows current → new, is editable, and can be unchecked.
 * Approve writes ONLY the checked fields (the server re-derives each row/column);
 * Dismiss rejects the whole batch. "Held" fields (a person with no record yet)
 * are shown read-only so nothing is silently lost.
 */
export function FeedIngestCard({
  workItem,
  onResolved,
}: {
  workItem: IngestWorkItem;
  onResolved: (id: string) => void;
}) {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const proposed = workItem.metadata?.proposed_updates ?? [];
  const held = workItem.metadata?.held ?? [];

  // Per-field edit state, keyed by dataPath: checked + (possibly edited) value.
  const [state, setState] = useState<Record<string, { on: boolean; value: string }>>(
    () =>
      Object.fromEntries(
        proposed.map((p) => [p.dataPath, { on: true, value: p.value }]),
      ),
  );
  const [busy, setBusy] = useState(false);

  const reviewer = identity
    ? { id: String(identity.id), name: identity.fullName }
    : undefined;

  const selectedCount = useMemo(
    () => Object.values(state).filter((s) => s.on && s.value.trim()).length,
    [state],
  );

  if (proposed.length === 0 && held.length === 0) return null;

  const run = async (
    fn: () => Promise<unknown>,
    ok: string | ((res: unknown) => string),
  ) => {
    setBusy(true);
    try {
      const res = await fn();
      onResolved(workItem.id);
      notify(typeof ok === 'function' ? ok(res) : ok, { type: 'success' });
      refresh();
    } catch (e) {
      notify(`Failed: ${e instanceof Error ? e.message : String(e)}`, {
        type: 'error',
      });
      setBusy(false);
    }
  };

  const handleApprove = () => {
    const updates: ApplyFieldUpdate[] = proposed
      .filter((p) => state[p.dataPath]?.on && state[p.dataPath]?.value.trim())
      .map((p) => ({ dataPath: p.dataPath, value: state[p.dataPath].value.trim() }));
    if (updates.length === 0) {
      notify('Select at least one field to save', { type: 'warning' });
      return;
    }
    void run(
      () => applyFields(workItem.id, updates, reviewer),
      // The server skips values it can't write — an ambiguous date like
      // "05/01/1948" that could be D/M or M/D, for example. Say so, rather than
      // reporting a count that includes fields which silently didn't save.
      (res) => {
        const { applied, skipped } = (res ?? {}) as { applied?: number; skipped?: number };
        const n = applied ?? updates.length;
        return skipped
          ? `Saved ${n} field(s) — ${skipped} skipped, check the flagged values`
          : `Saved ${n} field(s)`;
      },
    );
  };

  return (
    <div className="px-3 sm:px-4 py-2.5">
      <div className="rounded-xl border border-primary/25 bg-muted/40 p-3 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <ClipboardCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">
            Family sent info — review &amp; save
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          {proposed.map((p) => {
            const st = state[p.dataPath];
            return (
              <div key={p.dataPath} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={st?.on ?? true}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      [p.dataPath]: { ...s[p.dataPath], on: e.target.checked },
                    }))
                  }
                  className="h-3.5 w-3.5 shrink-0 accent-[var(--primary)]"
                />
                <span className="w-40 shrink-0 truncate text-xs text-muted-foreground">
                  {p.label}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {fmt(p.current_value)}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">→</span>
                <input
                  value={st?.value ?? ''}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      [p.dataPath]: { ...s[p.dataPath], value: e.target.value },
                    }))
                  }
                  disabled={!st?.on}
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                />
                {p.warning && (
                  <span title={p.warning}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {held.length > 0 && (
          <div className="mt-2 rounded-md border border-dashed border-amber-400/50 bg-amber-50/40 px-2.5 py-1.5 text-xs text-muted-foreground dark:bg-amber-950/20">
            <span className="font-medium text-amber-700 dark:text-amber-500">
              Needs a record first:
            </span>{' '}
            {held.map((h) => `${h.label} (${h.value})`).join(', ')} — add the person
            on the case, then re-send.
          </div>
        )}

        <div className="mt-2.5 flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleApprove}
            disabled={busy || selectedCount === 0}
            className="gap-1.5"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Save {selectedCount > 0 ? selectedCount : ''}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              void run(
                () => dismissWorkItem(workItem.id, reviewer),
                'Suggestion dismissed',
              )
            }
            disabled={busy}
            className="gap-1.5 text-muted-foreground"
          >
            <X className="h-3.5 w-3.5" />
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}
